import React, { useState, useEffect } from 'react'
import { useResume } from './ResumeProvider'
import { Textarea } from '~/lib/ui/textarea'
import { Button } from '~/lib/ui/button'
import { Toaster } from '~/lib/ui/toast'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
} from '~/components/shared/TabNav'
import {
  DocumentMagnifyingGlassIcon,
  IdentificationIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import Editor from './Editor/index'
import PdfViewerPanel from './PdfViewerPanel'
import { StyleEditor } from '~/lib/pdf'
import ContextWrapper from './ContextWrapper'
import SelectTemplatePanel, { TemplatePreview } from './SelectTemplatePanel'

const ResumeBuilder = ({ ...props }) => {
  return (
    <ContextWrapper {...props}>
      <ResumeBuilderInner />
    </ContextWrapper>
  )
}
const ResumeBuilderInner = () => {
  const {
    devMode,
    documentId,
    previewTemplateIndex,
    setPreviewTemplateIndex,
    uncategorizedData,
  } = useResume()

  const defaultActiveTab = documentId ? 'details' : 'chooseTemplate'
  const uncategorizedDataExists = Object.keys(uncategorizedData).length > 0

  return (
    <div className="flex w-full flex-col md:flex-row">
      <div className="md:basis-3/5">
        <TabNav
          className="px-4 pt-4 lg:px-8 xl:px-12"
          defaultActiveTab={defaultActiveTab}
          onChangeActiveTab={() => setPreviewTemplateIndex(null)}
          mobileMode="swipe"
        >
          <ButtonContainer>
            {uncategorizedDataExists && (
              <TabButton
                name="importResult"
                text="Import Result"
                Icon={DocumentTextIcon}
              />
            )}
            <TabButton
              name="chooseTemplate"
              text="Choose Template"
              Icon={DocumentMagnifyingGlassIcon}
            />
            <TabButton
              name="details"
              text="Details"
              Icon={IdentificationIcon}
            />
            <TabButton
              name="formattingAndDesign"
              text="Formatting & Design"
              Icon={ClipboardDocumentCheckIcon}
            />
            {devMode && (
              <TabButton name="text" text="Raw JSON" Icon={DocumentTextIcon} />
            )}
          </ButtonContainer>
          <PanelContainer className="md:min-h-[91vh]">
            <TabPanel name="chooseTemplate" className="bg-[#E0F2F9] pt-8">
              <SelectTemplatePanel />
            </TabPanel>
            <TabPanel name="details" className="pt-8">
              <Editor />
            </TabPanel>
            <TabPanel name="formattingAndDesign" className="pt-8">
              <StyleEditor />
            </TabPanel>
            <TabPanel name="text" className="pt-8">
              <TextEditor />
            </TabPanel>
            <TabPanel name="importResult" className="pt-8">
              <ImportResultPanel />
            </TabPanel>
          </PanelContainer>
        </TabNav>
      </div>
      <div id="resume-pdf-preview" className="relative md:flex-grow">
        <div className="w-full md:sticky md:left-0 md:top-0 md:h-screen">
          {previewTemplateIndex === null ? (
            <PdfViewerPanel />
          ) : (
            <RightPanelTemplatePreview />
          )}
        </div>
      </div>
      <Toaster />
    </div>
  )
}
export default ResumeBuilder

const RightPanelTemplatePreview = () => {
  const { previewTemplateIndex, setPreviewTemplateIndex } = useResume()

  return (
    <div className="flex flex-col bg-mine-shaft px-6 pt-8 md:h-full md:max-w-[35vw]">
      <div className="flex flex-row items-center justify-between">
        <h2 className="text-2xl font-bold text-white">
          Style {previewTemplateIndex + 1}
        </h2>
        <Button
          variant="ghost"
          className="text-white hover:bg-mine-shaft/80 hover:text-white"
          onClick={() => setPreviewTemplateIndex(null)}
        >
          Close
        </Button>
      </div>
      <TemplatePreview templateIndex={previewTemplateIndex} />
    </div>
  )
}
const ImportResultPanel = () => {
  const { uncategorizedData } = useResume()
  const [displayUncategorizedData, setDisplayUncategorizedData] = useState(
    JSON.stringify(uncategorizedData, null, 2).replace(/(?:\\\\n|\\\\t)/g, ' '),
  )

  return (
    <div className="flex h-full flex-col">
      <div>
        These resume sections and analysis were extracted from your resume but
        not imported.
      </div>
      <Textarea
        className="h-full w-full"
        value={displayUncategorizedData}
        onChange={(e) => setDisplayUncategorizedData(e.target.value)}
        disabled
      ></Textarea>
    </div>
  )
}

const TextEditor = () => {
  const { resumeDocument, setResumeDocument } = useResume()
  const [editorResume, setEditorResume] = useState(
    JSON.stringify(resumeDocument, null, 2),
  )

  const sync = () => {
    const updatedResume = JSON.parse(editorResume)
    setResumeDocument(updatedResume)
  }

  useEffect(() => {
    setEditorResume(JSON.stringify(resumeDocument, null, 2))
  }, [resumeDocument])

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-row justify-evenly">
        <Button onClick={sync}>Sync</Button>
      </div>
      <Textarea
        className="h-full w-full"
        value={editorResume}
        onChange={(e) => setEditorResume(e.target.value)}
      ></Textarea>
    </div>
  )
}
